import { useEffect, useState } from 'react'
import { Box, Button, Card, CardBody, CardHeader, Heading, HStack, Spinner, Text } from '@chakra-ui/react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { UserRoundCog } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { TicketRequestForm } from '@/components/forms/TicketRequestForm'
import ReassignModal from '@/components/helpdesk/ReassignModal'
import { useThemeColors } from '@/hooks/useThemeColors'
import { apiClient } from '@/lib/api/client'
import { useDisclosure } from '@chakra-ui/react'

type EditLocationState = { fromUpdateStatus?: boolean }

export default function AllTicketsEditPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const numericId = id ? Number(id) : undefined
  const [isValidating, setIsValidating] = useState(true)
  const [refreshKey, setRefreshKey] = useState(0)
  const [assignedToId, setAssignedToId] = useState<number | null>(null)
  const { isOpen: isReassignOpen, onOpen: onReassignOpen, onClose: onReassignClose } = useDisclosure()

  useEffect(() => {
    if (!numericId || Number.isNaN(numericId)) {
      navigate('/ticket-management/all-tickets', { replace: true })
    } else {
      setIsValidating(false)
    }
  }, [numericId, navigate])

  const fromUpdateStatus = Boolean((location.state as EditLocationState | undefined)?.fromUpdateStatus)

  const handleSuccess = () => {
    if (fromUpdateStatus && numericId != null && !Number.isNaN(numericId)) {
      navigate(`/ticket-management/all-tickets/${numericId}/update-status`)
      return
    }
    navigate('/ticket-management/all-tickets')
  }

  const handleCancel = () => {
    if (fromUpdateStatus && numericId != null && !Number.isNaN(numericId)) {
      navigate(`/ticket-management/all-tickets/${numericId}/update-status`)
      return
    }
    navigate('/ticket-management/all-tickets')
  }

  const handleReassignSuccess = () => {
    setRefreshKey((k) => k + 1)
    setAssignedToId(null)
  }

  const loadAssignedTo = async () => {
    if (!numericId) return
    try {
      const { data } = await apiClient.get(`/ticket-management/all-tickets/${numericId}`)
      const r = data?.data ?? data
      setAssignedToId(r.assigned_to ?? null)
    } catch {
      setAssignedToId(null)
    }
  }

  useEffect(() => {
    if (numericId) loadAssignedTo()
  }, [numericId, refreshKey])

  if (isValidating || !numericId) {
    return (
      <AdminLayout>
        <Box p={4} display="flex" alignItems="center" justifyContent="center" minH="200px">
          <Spinner size="lg" />
          <Text ml={3}>Loading...</Text>
        </Box>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
        <Box minW={0} display="flex" flexDirection="column" overflow="hidden" flex="1">
          <Card h="full" display="flex" flexDirection="column" flex="1" minH={0}>
            <CardHeader flexShrink={0}>
              <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={2}>
                <Box>
                  <Heading size="md">Edit Ticket</Heading>
                  <Text fontSize="sm" color={colors.text} mt={1}>
                    Update ticket request details
                  </Text>
                </Box>
                <Button
                  size="sm"
                  leftIcon={<UserRoundCog size={16} />}
                  variant="outline"
                  colorScheme="blue"
                  onClick={onReassignOpen}
                >
                  Reassign
                </Button>
              </HStack>
            </CardHeader>
            <CardBody overflowY="auto" flex="1" minH={0}>
              <TicketRequestForm
                ticketRequestId={numericId}
                onSuccess={handleSuccess}
                onCancel={handleCancel}
                refreshKey={refreshKey}
              />
            </CardBody>
          </Card>

          <ReassignModal
            isOpen={isReassignOpen}
            onClose={onReassignClose}
            ticketId={numericId}
            currentAssignedToId={assignedToId}
            onSuccess={handleReassignSuccess}
          />
        </Box>
      </Box>
    </AdminLayout>
  )
}
